import React from "react";

import type { AppRoute } from "../app/router";
import { AudienceSection } from "./AudienceSection";
import { BrandHeader } from "./BrandHeader";
import { DataRoomsWorkflow } from "./DataRoomsWorkflow";
import { EstimateSummary } from "./EstimateSummary";
import { FaqSection } from "./FaqSection";
import { LoginHandoff } from "./LoginHandoff";
import { QuoteAgentPanel } from "./QuoteAgentPanel";
import { PayrollSection } from "./PayrollSection";
import { ReportsShowcase } from "./ReportsShowcase";
import { ServicesSection } from "./ServicesSection";
import { SeoInfoCards } from "./SeoInfoCards";
import { SiteFooter } from "./SiteFooter";
import { SoftwarePricingSection } from "./SoftwarePricingSection";
import { StructuredMediaFigure } from "./StructuredMediaFigure";
import { WorkflowSection } from "./WorkflowSection";

type LandingPageProps = {
  route: AppRoute;
};

export function LandingPage({ route }: LandingPageProps) {
  const { content } = route;

  return (
    <div className="db-page">
      <a className="skip-link" href="#main-content">
        {content.navigation.skipToContentLabel}
      </a>
      <BrandHeader content={content} alternatePath={route.alternatePath} />

      <main id="main-content" className="db-main" aria-labelledby="page-title">
        <section className="db-first-viewport app-home-preview" id="home">
          <div className="db-first-viewport__narrative app-home-preview__narrative">
            <p className="section-eyebrow">{content.hero.eyebrow}</p>
            <h1 id="page-title">{content.hero.title}</h1>
            <p className="app-home-preview__summary">{content.hero.lead}</p>
            <div className="app-home-preview__actions" aria-label="Primary actions">
              <a className="db-button db-button--primary" href={content.hero.primaryCta.href}>
                {content.hero.primaryCta.label}
              </a>
              <a className="db-button db-button--secondary" href={content.hero.secondaryCta.href}>
                {content.hero.secondaryCta.label}
              </a>
            </div>
            <div className="app-home-preview__trust" aria-label="Accounting workstreams">
              {content.hero.trustCues.map((cue) => (
                <span key={cue}>{cue}</span>
              ))}
            </div>
            <StructuredMediaFigure
              assetId="software-overview"
              className="app-home-preview__media"
              loading="eager"
            />
          </div>

          <QuoteAgentPanel locale={content.locale} />
        </section>

        <AudienceSection content={content.audience} />
        <SeoInfoCards content={content.seoInfoCards} />
        <ServicesSection content={content.services} />
        <WorkflowSection content={content.accountingAgent} />
        <DataRoomsWorkflow content={content.dataRooms} locale={content.locale} />
        <ReportsShowcase content={content.reports} />
        <PayrollSection content={content.payroll} />
        <SoftwarePricingSection locale={content.locale} />
        <EstimateSummary content={content.estimate} locale={content.locale} />
        <LoginHandoff locale={content.locale} />
        <FaqSection content={content.faq} />
      </main>

      <SiteFooter content={content.footer} />
    </div>
  );
}
